/*
SiccasAjaxExtraction (siccas-ajax-extraction.js)

Purpose
Handles AJAX content extraction and displays it in a popup. Also provides reinitialization 
capability for dynamically loaded content, which is essential for integration with the cart drawer.

Related Files
- siccas-ajax-extraction.js - Main file containing SiccasAjaxExtraction class
- siccas-cart-drawer.js - Cart drawer enhancement that uses this extraction utility (SiccasCartAjaxLoader)

Required HTML Structure
- `.ajax-fetch__button[data-ajax-load="true"]` - Button to trigger AJAX loading
- `.ajax-fetch__item` - Parent element containing all settings

Data Attributes
- `data-ajax-url` - URL to load content from (required)
- `data-crop-content` - "true"/"false" to crop content (optional)
- `data-read-more-text` - Text for "Read more" link (optional)
- `data-open-in-new-tab` - "true"/"false" to open in new tab (optional)

HTML Example

<div class="ajax-fetch__item" 
     data-ajax-url="/page-url.html" 
     data-crop-content="true" 
     data-read-more-text="Read more" 
     data-open-in-new-tab="true">
  <button class="ajax-fetch__button" data-ajax-load="true">
    Open content
  </button>
</div>

How It Works
1. Creates a singleton instance with getInstance() method
2. Finds all buttons with class .ajax-fetch__button[data-ajax-load="true"]
3. Creates empty popup structure in body
4. On button click:
 - Gets URL from parent's data-ajax-url attribute
 - Loads content via AJAX
 - Extracts main content (main or .page-content)
 - Displays in popup
 - Adds "Read more" link if specified

Key Components
 - SiccasAjaxExtraction.getInstance() - Gets or creates singleton instance
 - reinitialize() - Public method to initialize new buttons in dynamically loaded content
 - showContentFromUrl() - Public method to programmatically show content from a URL
 - fetchContent() - Fetches and extracts content from a URL

Integration with SiccasCartAjaxLoader (siccas-cart-drawer.js)
 - SiccasCartAjaxLoader calls SiccasAjaxExtraction.getInstance().reinitialize() after loading product content into the cart drawer
 - This ensures any interactive elements in the loaded product content are properly initialized
 - The reinitialize() method finds and initializes any new .ajax-fetch__button elements that were added to the DOM

Popup Management
 - Creates a popup structure with overlay, content area, and close button
 - Handles opening, closing, and animation of the popup
 - Supports content cropping with "Read more" link

*/

class SiccasAjaxExtraction {
  constructor() {
    // Create a singleton instance
    if (SiccasAjaxExtraction.instance) {
      return SiccasAjaxExtraction.instance;
    }

    SiccasAjaxExtraction.instance = this;

    this.ajaxButtons = [];
    this.popupCreated = false;
    this.buttonClickHandlers = new WeakMap(); // Track event handlers to prevent duplicates
    this.init();
  }

  // Static method to get the instance
  static getInstance() {
    if (!SiccasAjaxExtraction.instance) {
      new SiccasAjaxExtraction();
    }
    return SiccasAjaxExtraction.instance;
  }

  init() {
    // Create popup if it doesn't exist
    if (!this.popupCreated) {
      this.createPopup();
    }

    // Initialize buttons
    this.findAndInitButtons();
  }

  // Find all buttons and initialize them
  findAndInitButtons() {
    const buttons = document.querySelectorAll(
      '.ajax-fetch__button[data-ajax-load="true"]'
    );

    if (!buttons.length) return;

    buttons.forEach((button) => {
      // Check if we already have a handler for this button
      if (!this.buttonClickHandlers.has(button)) {
        // Create a bound handler for this button
        const handler = this.handleButtonClick.bind(this);
        this.buttonClickHandlers.set(button, handler);

        // Add event listener
        button.addEventListener("click", handler);

        // Add to tracked buttons
        this.ajaxButtons.push(button);
      }
    });
  }

  // Public method to reinitialize when new content is added to the DOM
  reinitialize() {
    this.findAndInitButtons();
  }

  createPopup() {
    // Create popup elements
    const popup = document.createElement("div");
    popup.className = "ajax-fetch-popup";

    const overlay = document.createElement("div");
    overlay.className = "ajax-fetch-popup__overlay";
    overlay.addEventListener("click", this.closePopup.bind(this));

    const content = document.createElement("div");
    content.className = "ajax-fetch-popup__content";

    const closeButton = document.createElement("button");
    closeButton.className = "ajax-fetch-popup__close";
    closeButton.setAttribute("aria-label", "Close");
    closeButton.innerHTML =
      '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" class="icon icon-close" fill="none" viewBox="0 0 18 17"><path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"></path></svg>';
    closeButton.addEventListener("click", this.closePopup.bind(this));

    // Append elements
    content.appendChild(closeButton);
    popup.appendChild(overlay);
    popup.appendChild(content);
    document.body.appendChild(popup);

    // Store references
    this.popup = popup;
    this.popupContent = content;
    this.popupCreated = true;
  }

  handleButtonClick(event) {
    const button = event.currentTarget;
    const valueItem = button.closest(".ajax-fetch__item");
    const ajaxUrl = valueItem.getAttribute("data-ajax-url");

    // Get additional attributes from parent element
    this.cropContent = valueItem.getAttribute("data-crop-content") === "true";
    this.readMoreText = valueItem.getAttribute("data-read-more-text");
    this.openInNewTab =
      valueItem.getAttribute("data-open-in-new-tab") === "true";
    this.currentAjaxUrl = ajaxUrl;

    if (!ajaxUrl) {
      return;
    }

    // Show loading state
    button.classList.add("loading");

    this.fetchContent(ajaxUrl)
      .then((content) => {
        // Populate and show popup
        this.showPopupWithContent(content);

        // Remove loading state
        button.classList.remove("loading");
      })
      .catch((error) => {
        console.error("Error fetching content:", error);
        button.classList.remove("loading");
      });
  }

  async fetchContent(url) {
    // Get locale prefix from Shopify routes
    const routesRoot = window.Shopify?.routes?.root || '/';

    // Add locale prefix if URL starts with / and routesRoot has locale
    let localizedUrl = url;
    if (url.startsWith('/') && routesRoot !== '/') {
      localizedUrl = routesRoot + url.substring(1);
    }

    const response = await fetch(localizedUrl);
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    const html = await response.text();

    // Parse the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    // Find main content (adjust selector if needed)
    const mainContent =
      doc.querySelector("main") || doc.querySelector(".page-content");

    if (mainContent) {
      return mainContent;
    } else {
      return doc.body;
    }
  }

  // Public method that can be called from other scripts
  showContentFromUrl(url, options = {}) {
    this.cropContent = options.cropContent || false;
    this.readMoreText = options.readMoreText || null;
    this.openInNewTab = options.openInNewTab || false;
    this.currentAjaxUrl = url;

    return this.fetchContent(url).then((content) => {
      this.showPopupWithContent(content);
      return content;
    });
  }

  showPopupWithContent(content) {
    // Remove closing class when opening
    this.popup.classList.remove("ajax-fetch-popup--closing");

    const closeButton = this.popupContent.querySelector(
      ".ajax-fetch-popup__close"
    );
    this.popupContent.innerHTML = "";
    this.popupContent.appendChild(closeButton);

    const contentClone = content.cloneNode(true);

    // Create additional wrapper for content
    const contentWrapper = document.createElement("div");
    contentWrapper.className = "ajax-fetch-popup__content-wrapper";

    // Add cropped class if crop content is enabled
    if (this.cropContent) {
      contentWrapper.classList.add(
        "ajax-fetch-popup__content-wrapper--cropped"
      );
    }

    // Create inner wrapper for the actual content
    const innerWrapper = document.createElement("div");
    innerWrapper.className = "ajax-fetch-popup__inner-content";

    // Move content to inner wrapper
    innerWrapper.appendChild(contentClone);

    // Add inner wrapper to content wrapper
    contentWrapper.appendChild(innerWrapper);

    // Add "Read more" link inside content wrapper if crop content is enabled
    if (this.cropContent && this.readMoreText && this.currentAjaxUrl) {
      const readMoreLink = document.createElement("a");
      readMoreLink.href = this.currentAjaxUrl;
      readMoreLink.className = "ajax-fetch-popup__read-more";
      readMoreLink.textContent = this.readMoreText;

      if (this.openInNewTab) {
        readMoreLink.setAttribute("target", "_blank");
      }

      contentWrapper.appendChild(readMoreLink);
    }

    // Add content wrapper to popup
    this.popupContent.appendChild(contentWrapper);

    document.body.classList.add("ajax-fetch-open");
    this.popup.classList.add("ajax-fetch-popup--active");
  }

  closePopup() {
    document.body.classList.remove("ajax-fetch-open");
    this.popup.classList.add("ajax-fetch-popup--closing");

    // Remove active class after animation
    setTimeout(() => {
      this.popup.classList.remove("ajax-fetch-popup--active");
      this.popup.classList.remove("ajax-fetch-popup--closing");
    }, 300);
  }
}

// Initialize global instance when DOM is loaded
let siccasAjaxExtractionInstance = null;

document.addEventListener("DOMContentLoaded", function () {
  siccasAjaxExtractionInstance = SiccasAjaxExtraction.getInstance();
});

// Export global instance for other scripts
window.SiccasAjaxExtraction = SiccasAjaxExtraction;
